import type { TopWindowKey } from './streamOrderMapping'
export type ArtistOrder = 'time' | 'top' | 'hot'

export const ARTIST_ORDERS: Array<{ key: ArtistOrder; label: string; tip: string }> = [
    { key: 'time', label: 'New', tip: 'Newest artists on the radio' },
    { key: 'hot', label: 'Hot', tip: 'Artists with the most recent upvalue on the radio' },
    { key: 'top', label: 'Top', tip: 'Artists with the most upvalue on their radio songs' },
]

export type SongOrder = 'plays' | 'recent'

export const SONG_ORDERS: Array<{ key: SongOrder; label: string; tip: string }> = [
    { key: 'plays', label: 'Top', tip: 'Songs with the most plays on the radio' },
    { key: 'recent', label: 'New', tip: 'Songs played on the radio most recently' },
]

export type RadioArtist = {
    id: string
    name: string | null
    avatar_urls?: { small?: string; original?: string } | null
    rank?: number | null
    play_count: number
    track_count: number
    upvalue_sats: number
    first_played_at?: number | null
    last_played_at?: number | null
}

export type RadioArtistTrack = {
    answer_id: string
    quest_id?: string | null
    title?: string
    play_count: number
    last_played_at?: number | null
}

export type RadioArtistProfile = RadioArtist & { tracks: RadioArtistTrack[] }

// Resolved against the configured API endpoint (gon.api), like the other radio
// reads: the split-origin frontends do not proxy /api/v1.
const artistsBaseUrl = () =>
    (typeof gon !== 'undefined' ? gon?.api?.radioArtists : null) || '/api/v1/radio/artists'

export function artistsUrl(order: ArtistOrder, page: number) {
    return `${artistsBaseUrl()}?order=${order}&page=${page}`
}

// New, Hot (recent upvalue counts most), or the Top dropdown's choices: Top
// with a window (or none = all time), or Top New (all-time Top, songs whose
// thread you haven't seen first).
export type DiscoverOrder = 'time' | 'hot' | 'top' | 'top_new'

export const DISCOVER_BUTTONS: Array<{
    key: 'time' | 'hot'
    label: string
    tip: string
    icon: string
}> = [
    { key: 'time', label: 'New', tip: 'Newest songs on Treechat', icon: 'fa fa-clock-o' },
    { key: 'hot', label: 'Hot', tip: 'Songs with the most recent upvalue', icon: 'fa fa-fire' },
]

// The gap between Discover's songs. Matches the 28px under a song card in the
// non-windowed lists (radio-artists.module.scss .songCard).
export const DISCOVER_SONG_GAP_PX = 28
// Phones pad every panel's scroll container 15px a side and songs run past it.
const MOBILE_PANEL_SIDE_PADDING_PX = 15
const SONG_CARD_BORDER_PX = 1

// A Discover row's exact height: the 16:9 media at the card's width, plus the
// card's top and bottom borders. Desktop has side borders; phones run edge to edge.
export function discoverSongRowHeight(listWidth: number, mobile: boolean) {
    if (!(listWidth > 0)) return 1
    const mediaWidth = mobile
        ? listWidth + MOBILE_PANEL_SIDE_PADDING_PX * 2
        : listWidth - SONG_CARD_BORDER_PX * 2
    return Math.ceil((mediaWidth * 9) / 16) + SONG_CARD_BORDER_PX * 2
}

export function isDiscoverTop(order: DiscoverOrder) {
    return order === 'top' || order === 'top_new'
}

export type DiscoverSong = {
    answer_id: string
    quest_id?: string | null
    title?: string
    // Sats upvalued in the response's window (all time when it has none).
    upvalue_sats: number
    created_at: number
}

export function discoverUrl(order: DiscoverOrder, topWindow: TopWindowKey | null, page: number) {
    const base =
        (typeof gon !== 'undefined' ? gon?.api?.radioDiscover : null) || '/api/v1/radio/discover'
    const windowParam = order === 'top' && topWindow ? `&top_window=${topWindow}` : ''
    return `${base}?order=${order}${windowParam}&page=${page}`
}

export function mySongsUrl(page: number) {
    const base =
        (typeof gon !== 'undefined' ? gon?.api?.radioMySongs : null) || '/api/v1/radio/my_songs'
    return `${base}?page=${page}`
}

// What a song card's sats count, after the amount: "today", "this week", …;
// nothing for all time.
const DISCOVER_WINDOW_SUFFIX: Partial<Record<TopWindowKey, string>> = {
    '1h': 'this hour',
    '24h': 'today',
    week: 'this week',
    month: 'this month',
    year: 'this year',
}

export function discoverSatsSuffix(window: string | null | undefined) {
    return (window && DISCOVER_WINDOW_SUFFIX[window as TopWindowKey]) || ''
}

export function artistUrl(id: string, songOrder?: SongOrder) {
    const url = `${artistsBaseUrl()}/${encodeURIComponent(id)}`
    return songOrder ? `${url}?song_order=${songOrder}` : url
}

export function formatPlays(count: number) {
    return `${count} ${count === 1 ? 'play' : 'plays'}`
}

export function formatSongs(count: number) {
    return `${count} ${count === 1 ? 'song' : 'songs'}`
}

const SATS_PER_BSV = 100_000_000
export const USD_THRESHOLD = 0.1

// Earned amounts read in dollars once they are worth ten cents or more (and a
// rate is known), else in sats, whatever the viewer's own unit preference.
export function formatEarned(sats: number, exchangeRate: number | null | undefined) {
    const usd = exchangeRate ? (sats / SATS_PER_BSV) * exchangeRate : null
    if (usd != null && usd >= USD_THRESHOLD - 1e-9) {
        return usd.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
    }
    return `${Math.round(sats).toLocaleString()} sats`
}

export function sortSongs(tracks: RadioArtistTrack[], order: SongOrder) {
    const key = (track: RadioArtistTrack) =>
        order === 'recent' ? track.last_played_at ?? 0 : track.play_count
    return [...tracks].sort((a, b) => key(b) - key(a))
}
